import * as React from 'react';
import styled, {css} from 'styled-components';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import CardContext from './CardContext';
import LessonBuilderContext from 'components/LessonBuilder/LessonBuilderContext';
import BoxContainer from 'containers/BoxContainer';
import DragControl from 'components/DragControl';
import ControlButton from 'components/ControlButton';
import Separator from './components/Separator';
import InputNumber from 'components/InputNumber';
import * as Template from './templates';
import {transition} from 'helpers/css';
import {
  ContentObject,
  ContentType,
  DocumentValue,
  GalleryValue,
  ImageValue,
  TextValue,
  VideoValue,
} from 'api/lesson/types';
import {IconCopy, IconReset, IconTrash} from 'icons';

export interface CardProps extends ContentObject {
  index: number;
}

export const SeparatorContainer = styled.div<DraggableStateSnapshot>`
  ${transition()};
  
  ${({isDragging, isDropAnimating}) => (isDragging && !isDropAnimating) && css`
    opacity: 0;
    visibility: hidden;
  `};
`;

const Toolbar = styled.div`
  padding: 10px;
  justify-content: space-between;
  align-items: center;
  grid-auto-flow: column;
  display: grid;
`;

const Controls = styled.div`
  grid-auto-flow: column;
  align-items: center;
  display: inline-grid;
`;

const Content = styled.div`
  padding: 0 20px 20px 20px;
  box-sizing: border-box;
`;

/**
 * This component renders a content card.
 * @param {CardProps} props
 * @return {React.ReactElement<CardProps>}
 * @constructor
 */
export default function Card(
    props: CardProps,
): React.ReactElement<CardProps> {
  const {lesson, setLesson} = React.useContext(LessonBuilderContext);
  const content = lesson?.content || [];

  /**
   * Render toolbar.
   * @param {DraggableProvided} provided
   * @param {DraggableStateSnapshot} snapshot
   * @return {JSX.Element}
   */
  function renderToolbar(
      provided?: DraggableProvided,
      snapshot?: DraggableStateSnapshot,
  ): JSX.Element {
    /**
     * Render drag control.
     * @return {JSX.Element}
     */
    function renderDragControl(): JSX.Element {
      return (
        <DragControl
          provided={provided}
          snapshot={snapshot}
        />
      );
    }

    /**
     * Render controls
     * @return {JSX.Element}
     */
    function renderControls(): JSX.Element {
      /**
       * Render value length.
       * @return {JSX.Element}
       */
      function renderValueLength(): JSX.Element | undefined {
        /**
         * This change event handler.
         * @param {number} count
         */
        function handleChange(count: number): void {
          const value = props.value as ImageValue;
          value.fill(undefined);
          // content[props.index].value.f
        }

        return props.contentType === ContentType.Image ? (
          <InputNumber
            minLength={0}
            maxLength={3}
            defaultValue={props.value?.length}
            onChange={handleChange}
          />
        ) : undefined;
      }

      /**
       * Render reset control
       * @return {JSX.Element}
       */
      function renderReset(): JSX.Element {
        /**
         * Handle click on control
         */
        function handleClick(): void {
          content[props.index].value = undefined;

          (lesson && setLesson) && setLesson({
            ...lesson,
            content,
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconReset />
          </ControlButton>
        );
      }

      /**
       * Render copy control
       * @return {JSX.Element}
       */
      function renderCopy(): JSX.Element {
        /**
         * Handle click on control
         */
        function handleClick(): void {
          content.splice(props.index, 0, {
            id: props.id,
            contentType: props.contentType,
            value: props.value,
          });

          (lesson && setLesson) && setLesson({
            ...lesson,
            content,
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconCopy />
          </ControlButton>
        );
      }

      /**
       * Render trash control
       * @return {JSX.Element}
       */
      function renderTrash(): JSX.Element {
        /**
         * Handle click on control
         */
        function handleClick(): void {
          content.splice(props.index, 1);

          (lesson && setLesson) && setLesson({
            ...lesson,
            content,
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconTrash />
          </ControlButton>
        );
      }

      return (
        <Controls>
          {renderReset()}
          {renderCopy()}
          {renderTrash()}
        </Controls>
      );
    }

    return (
      <Toolbar>
        {renderDragControl()}
        {renderControls()}
      </Toolbar>
    );
  }

  /**
   * Render separator.
   * @param {DraggableStateSnapshot} snapshot
   * @return {JSX.Element}
   */
  function renderSeparator(snapshot: DraggableStateSnapshot): JSX.Element {
    return (
      <SeparatorContainer {...snapshot}>
        <Separator index={props.index} />
      </SeparatorContainer>
    );
  }

  /**
   * Render template.
   * @return {JSX.Element}
   */
  function renderTemplate(): JSX.Element {
    switch (props.contentType) {
      case ContentType.Text: return (
        <Template.Text value={props.value as TextValue} />
      );
      case ContentType.Image: return (
        <Template.Image value={props.value as ImageValue} />
      );
      case ContentType.Video: return (
        <Template.Video value={props.value as VideoValue} />
      );
      case ContentType.Document: return (
        <Template.Document value={props.value as DocumentValue} />
      );
      case ContentType.Gallery: return (
        <Template.Gallery value={props.value as GalleryValue} />
      );
    }
  }

  const {Provider} = CardContext;

  return (
    <Provider value={props}>
      <Draggable draggableId={props.id} index={props.index}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            {renderSeparator(snapshot)}
            <BoxContainer>
              {renderToolbar(provided, snapshot)}
              <Content>
                {renderTemplate()}
              </Content>
            </BoxContainer>
          </div>
        )}
      </Draggable>
    </Provider>
  );
}
